import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AppHeader } from '@/components/layout/AppHeader';
import { setProfessorToken } from '../authCookie';

interface ProfessorData {
  professor_netid: string;
  professor_name: string;
  professor_email: string;
}

interface ProfessorAuthProps {
  backendUrl?: string;
  onAuthenticated: (professor: ProfessorData) => void;
}

const ProfessorAuth: React.FC<ProfessorAuthProps> = ({
  backendUrl = 'http://localhost:8000',
  onAuthenticated,
}) => {
  const [step, setStep] = useState<'email' | 'verify'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [emailSentTo, setEmailSentTo] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleEmailSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmedEmail = email.trim().toLowerCase();

    if (!trimmedEmail) {
      setError('Please enter your email.');
      return;
    }

    setIsSubmitting(true);
    setStatus(null);
    setError(null);

    try {
      const response = await fetch(`${backendUrl}/api/auth/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: trimmedEmail }),
      });

      const data = await response.json();

      if (!response.ok) {
        const detail = data?.detail || 'Request failed. Please try again.';
        setError(detail);
        return;
      }

      setEmailSentTo(data.email_sent_to);
      setStatus('Verification code sent! Check your email.');
      setStep('verify');
    } catch (err) {
      console.error('Error during login start:', err);
      setError('Unable to reach the server. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifySubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmedCode = code.trim();

    if (!trimmedCode) {
      setError('Please enter the verification code.');
      return;
    }

    setIsSubmitting(true);
    setStatus(null);
    setError(null);

    try {
      const response = await fetch(`${backendUrl}/api/auth/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email.trim().toLowerCase(), code: trimmedCode }),
      });

      const data = await response.json();

      if (!response.ok) {
        const detail = data?.detail || 'Verification failed. Please try again.';
        setError(detail);
        return;
      }

      if (data.session_token) {
        setProfessorToken(data.session_token);
      }
      setStatus('Login successful!');
      onAuthenticated({
        professor_netid: data.professor_netid,
        professor_name: data.professor_name,
        professor_email: data.professor_email,
      });
    } catch (err) {
      console.error('Error during verification:', err);
      setError('Unable to reach the server. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToEmail = () => {
    setStep('email');
    setCode('');
    setError(null);
    setStatus(null);
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <AppHeader
        right={
          <Button variant="ghost" onClick={() => navigate('/join')}>
            Join as a student
          </Button>
        }
      />
      <main className="flex flex-1 items-center justify-center px-6 py-16">
        <div className="w-full max-w-md rounded-2xl border bg-card p-8 shadow-soft sm:p-10">
          <h1 className="text-2xl font-bold tracking-tight">Professor sign in</h1>
          <p className="mt-1.5 text-sm text-muted-foreground">
            {step === 'email'
              ? 'Enter your email and we\'ll send you a one-time code.'
              : `Enter the code we sent to ${emailSentTo}`}
          </p>

          <div className="mt-6 space-y-3 empty:hidden">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            {status && (
              <Alert variant="success">
                <AlertDescription>{status}</AlertDescription>
              </Alert>
            )}
          </div>

          {step === 'email' ? (
            <form onSubmit={handleEmailSubmit} className="mt-6 space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="ada.lovelace@yale.edu"
                  autoComplete="email"
                  className="h-12 rounded-lg text-base md:text-base"
                />
              </div>
              <Button type="submit" variant="brand" size="lg" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? 'Sending code...' : 'Continue'}
                {!isSubmitting && <ArrowRight aria-hidden />}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerifySubmit} className="mt-6 space-y-5">
              <div className="space-y-2">
                <Label htmlFor="verification-code">Verification code</Label>
                <Input
                  id="verification-code"
                  type="text"
                  inputMode="numeric"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="123456"
                  autoComplete="one-time-code"
                  maxLength={6}
                  className="h-14 rounded-lg text-center text-2xl font-semibold tracking-[0.35em] placeholder:tracking-[0.35em] placeholder:text-muted-foreground/50 md:text-2xl"
                />
                <p className="text-xs text-muted-foreground">
                  Didn&apos;t get it within 2 minutes? Check your Spam or Junk folder.
                </p>
              </div>
              <Button type="submit" variant="brand" size="lg" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? 'Verifying...' : 'Verify and sign in'}
              </Button>
              <Button type="button" variant="ghost" className="w-full" onClick={handleBackToEmail}>
                Use a different email
              </Button>
            </form>
          )}

          <p className="mt-6 text-center text-xs text-muted-foreground">Not registered yet? Email neil.mathew@yale.edu or bukhosi.dube@yale.edu for access.</p>
        </div>
      </main>
    </div>
  );
};

export default ProfessorAuth;
